import { createTheme, rem, type MantineThemeOverride } from '@mantine/core';
import { ACCENT } from './palette.js';

/**
 * Две темы поверх общей палитры.
 *
 * CLAUDE.md: водительская часть и портал — разные наборы компонентов поверх
 * общей темы. Среды противоположны: у водителя перчатки, солнце и одна рука,
 * у менеджера большой экран, мышь и плотность информации.
 */

const shared = {
  /** Стоковая палитра Mantine: свои тюплы не заводим (см. palette.ts). */
  primaryColor: ACCENT,
  defaultRadius: 'md' as const,
  /**
   * Восьмой оттенок, а не шестой: белый текст на blue-6 даёт 3.6:1 и не
   * проходит AA для мелкого шрифта. У водителя к этому добавляется солнце.
   */
  primaryShade: { light: 8, dark: 4 } as const,
};

const UI_FONT =
  "'Inter Tight Variable', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
const MONO_FONT = "'JetBrains Mono Variable', ui-monospace, 'SF Mono', Menlo, monospace";

/** Водитель: крупно, контрастно, зоны нажатия под перчатку. */
export const driverTheme: MantineThemeOverride = createTheme({
  ...shared,
  fontFamilyMonospace: MONO_FONT,
  fontSizes: { xs: rem(14), sm: rem(16), md: rem(18), lg: rem(21), xl: rem(25) },
  lineHeights: { md: '1.5' },
  headings: {
    sizes: {
      h1: { fontSize: rem(30), lineHeight: '1.2' },
      h2: { fontSize: rem(24), lineHeight: '1.25' },
      h3: { fontSize: rem(20), lineHeight: '1.3' },
    },
  },
  radius: { md: rem(12), lg: rem(16) },
  components: {
    Button: {
      defaultProps: { size: 'xl' },
      styles: { root: { minHeight: rem(56), fontWeight: 600 } },
    },
    ActionIcon: {
      defaultProps: { size: 'xl' },
      styles: { root: { minHeight: rem(56), minWidth: rem(56) } },
    },
    TextInput: { defaultProps: { size: 'xl' } },
    PinInput: { defaultProps: { size: 'xl' } },
    Textarea: { defaultProps: { size: 'lg' } },
    Card: { defaultProps: { padding: 'lg', radius: 'lg', withBorder: true } },
  },
});

/**
 * Портал: плотность информации, сканирование глазами, мышь.
 *
 * Роль не является тяжестью, поэтому её бейдж нейтральный: цвет в этом
 * интерфейсе означает состояние, а не категорию. Если раскрасить роли,
 * критический дефект перестанет выделяться среди цветных плашек.
 */
export const portalTheme: MantineThemeOverride = createTheme({
  ...shared,
  fontFamily: UI_FONT,
  fontFamilyMonospace: MONO_FONT,
  headings: {
    fontFamily: UI_FONT,
    sizes: {
      h1: { fontSize: rem(22), lineHeight: '1.25', fontWeight: '650' },
      h2: { fontSize: rem(17), lineHeight: '1.3', fontWeight: '620' },
      h3: { fontSize: rem(14), lineHeight: '1.35', fontWeight: '600' },
    },
  },
  fontSizes: { xs: rem(11), sm: rem(12.5), md: rem(13.5), lg: rem(15), xl: rem(18) },
  lineHeights: { xs: '1.4', sm: '1.45', md: '1.5', lg: '1.5', xl: '1.4' },
  radius: { xs: rem(3), sm: rem(5), md: rem(7), lg: rem(10), xl: rem(14) },
  components: {
    Button: {
      defaultProps: { size: 'sm', radius: 'sm' },
      styles: { root: { fontWeight: 560, letterSpacing: '-0.005em' } },
    },
    TextInput: { defaultProps: { size: 'sm' } },
    Textarea: { defaultProps: { size: 'sm' } },
    Select: { defaultProps: { size: 'sm' } },
    Badge: {
      defaultProps: { variant: 'light', radius: 'sm' },
      styles: { label: { fontWeight: 600, letterSpacing: '0.01em' } },
    },
    Table: {
      defaultProps: { highlightOnHover: true, verticalSpacing: 'xs', horizontalSpacing: 'md' },
    },
    Card: { defaultProps: { padding: 'md', radius: 'md', withBorder: true } },
    Modal: {
      defaultProps: { radius: 'md', centered: true, overlayProps: { backgroundOpacity: 0.45, blur: 2 } },
    },
    Alert: { defaultProps: { radius: 'sm', variant: 'light' } },
  },
});
